import type { IGame, InputAction, GameState } from "../../core/types";
import { clamp } from "../../core/Time";

type Obstacle = { x: number; y: number; r: number; vy: number };

export class DodgeGame implements IGame {
  private state: GameState = { status: "ready" };

  private player = { x: 0, y: 0, r: 18 };
  private obstacles: Obstacle[] = [];

  private spawnT = 0;
  private t = 0;
  private score = 0;

  reset(): void {
    this.state = { status: "ready" };
    this.obstacles = [];
    this.spawnT = 0;
    this.t = 0;
    this.score = 0;
  }

  getState(): GameState {
    if (this.state.status === "running") return { status: "running", score: this.score };
    if (this.state.status === "gameover") return this.state;
    return { status: "ready" };
  }

  onAction(action: InputAction): void {
    if (action.type === "move") {
      this.player.x = action.x;
      this.player.y = action.y;
      return;
    }

    if (action.type === "tap" || (action.type === "keydown" && action.key === " ")) {
      if (this.state.status === "ready") {
        this.state = { status: "running", score: 0 };
        return;
      }
      if (this.state.status === "gameover") {
        this.reset();
        this.state = { status: "running", score: 0 };
        return;
      }
    }

    // fallback clavier : flèches
    if (action.type === "keydown") {
      const step = 24;
      if (action.key === "ArrowLeft") this.player.x -= step;
      if (action.key === "ArrowRight") this.player.x += step;
      if (action.key === "ArrowUp") this.player.y -= step;
      if (action.key === "ArrowDown") this.player.y += step;
    }
  }

  update(dt: number): void {
    // dt peut être 0 dans certains appels : on protège
    if (dt <= 0) return;

    this.t += dt;

    // score uniquement en running
    if (this.state.status === "running") {
      this.score = Math.floor(this.t * 10);
    } else {
      return;
    }

    // spawn obstacles (accélère avec le temps)
    this.spawnT -= dt;
    const spawnEvery = clamp(0.65 - this.t * 0.015, 0.18, 0.65);
    if (this.spawnT <= 0) {
      this.spawnT = spawnEvery;
      this.spawnObstacle();
    }

    // update obstacles
    for (const o of this.obstacles) {
      o.y += o.vy * dt;
    }

    // remove off-screen (géré côté render via canvas dims; ici on laisse)
  }

  render(ctx: CanvasRenderingContext2D): void {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;

    // init player position si jamais (premier render)
    if (this.player.x === 0 && this.player.y === 0) {
      this.player.x = w / 2;
      this.player.y = h * 0.72;
    }

    // clamp player within bounds
    this.player.x = clamp(this.player.x, this.player.r, w - this.player.r);
    this.player.y = clamp(this.player.y, this.player.r, h - this.player.r);

    // background
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(255,255,255,0.03)";
    ctx.fillRect(0, 0, w, h);

    // subtle grid
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    const step = Math.max(40, Math.floor(Math.min(w, h) / 18));
    for (let x = 0; x < w; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 0; y < h; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }

    // obstacles
    const keep: Obstacle[] = [];
    for (const o of this.obstacles) {
      // collision
      if (this.state.status === "running") {
        const dx = o.x - this.player.x;
        const dy = o.y - this.player.y;
        const rr = o.r + this.player.r;
        if (dx * dx + dy * dy <= rr * rr) {
          this.state = { status: "gameover", score: this.score, best: 0 };
        }
      }

      // draw
      ctx.fillStyle = "rgba(239,68,68,0.85)";
      ctx.beginPath();
      ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
      ctx.fill();

      // keep if still visible
      if (o.y < h + o.r) keep.push(o);
    }
    this.obstacles = keep;

    // player
    ctx.fillStyle = "rgba(34,197,94,0.90)";
    ctx.beginPath();
    ctx.arc(this.player.x, this.player.y, this.player.r, 0, Math.PI * 2);
    ctx.fill();

    // state text (sur canvas, léger)
    ctx.fillStyle = "rgba(229,231,235,0.85)";
    ctx.font = `${Math.floor(Math.max(14, Math.min(w, h) / 40))}px system-ui`;
    if (this.state.status === "ready") {
      ctx.fillText("Espace / clic pour démarrer", 24, h - 26);
    } else if (this.state.status === "gameover") {
      ctx.fillText("Collision — Espace pour rejouer", 24, h - 26);
    }
  }

  private spawnObstacle() {
    // on spawn en haut avec vitesse croissante
    // (canvas dims non accessibles ici, on se base sur dernière position / valeurs connues)
    // Solution simple : valeurs relatives, puis clamp au render via collision/keep.
    // On prend une largeur "virtuel" en se basant sur player.x (déjà dans canvas coords)
    const x = this.player.x + (Math.random() * 520 - 260);

    const r = 10 + Math.random() * 14;
    const vy = 220 + this.t * 22 + Math.random() * 60;

    this.obstacles.push({ x, y: -r - 10, r, vy });
  }
}
